import React, { useState } from 'react';

const SECTIONS = [
  { id: 'use', title: 'Use of Service' },
  { id: 'account', title: 'Account Responsibility' },
  { id: 'ip', title: 'Intellectual Property' },
  { id: 'billing', title: 'Subscriptions & Billing' },
  { id: 'data', title: 'Your Data' },
  { id: 'prohibited', title: 'Prohibited Conduct' },
  { id: 'termination', title: 'Termination' },
  { id: 'force-majeure', title: 'Force Majeure' },
  { id: 'disclaimer', title: 'Disclaimer' },
  { id: 'feedback', title: 'Feedback' },
  { id: 'liability', title: 'Limitation of Liability' },
  { id: 'indemnification', title: 'Indemnification' },
  { id: 'governing-law', title: 'Governing Law' },
  { id: 'changes', title: 'Changes to These Terms' },
  { id: 'general', title: 'General Provisions' },
  { id: 'contact', title: 'Contact Us' },
];

function scrollTo(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export default function TermsPage() {
  const [activeSection, setActiveSection] = useState('use');

  return (
    <div style={{ minHeight: '100vh', background: '#ffffff', fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', color: '#111827' }}>
      <style>{`
        @media (max-width: 768px) {
          .privacy-body-grid {
            grid-template-columns: 1fr !important;
            gap: 24px !important;
            padding: 32px 20px 64px !important;
          }
          .privacy-toc {
            position: static !important;
            border-bottom: 1px solid #e5e7eb;
            padding-bottom: 16px;
            margin-bottom: 8px;
          }
          .privacy-toc > div:last-child {
            display: flex !important;
            flex-wrap: wrap !important;
            gap: 6px !important;
            border-left: none !important;
          }
          .privacy-toc button {
            padding: 5px 10px !important;
            border: 1px solid #e5e7eb !important;
            border-radius: 999px !important;
            margin-left: 0 !important;
          }
        }
      `}</style>

      {/* ── NAV ── */}
      <header style={{
        background: '#ffffff', borderBottom: '1px solid #e5e7eb', padding: '0 16px',
        height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 100,
      }}>
        <a href="/" style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none' }}>
          <img src="/logo-icon.png" alt="CloudAIRambo logo" style={{ width: 34, height: 34, objectFit: 'contain' }}
            onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
          <span style={{ fontWeight: 700, fontSize: '18px', color: '#111827', letterSpacing: '-0.01em' }}>CloudAIRambo</span>
        </a>
        <a href="/" style={{ fontSize: '14px', color: '#6b7280', textDecoration: 'none', fontWeight: 500 }}
          onMouseEnter={e => (e.currentTarget.style.color = '#111827')}
          onMouseLeave={e => (e.currentTarget.style.color = '#6b7280')}
        >← Back to home</a>
      </header>

      {/* ── HERO ── */}
      <div style={{ textAlign: 'center', padding: '64px 24px 48px', borderBottom: '1px solid #e5e7eb', background: '#fafafa' }}>
        <img src="/logo.png" alt="CloudAIRambo" style={{ height: 80, width: 'auto', objectFit: 'contain', margin: '0 auto 24px', display: 'block' }} />
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: '6px',
          background: '#f0f4ff', border: '1px solid #c7d4fe', borderRadius: '999px',
          padding: '5px 14px', fontSize: '12px', color: '#4f46e5', fontWeight: 600, marginBottom: '24px',
        }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#6366f1', display: 'inline-block' }} />
          Legal
        </div>
        <h1 style={{ fontSize: 'clamp(28px, 8vw, 44px)', fontWeight: 800, color: '#111827', margin: '0 0 12px', letterSpacing: '-0.02em' }}>
          Terms of Service
        </h1>
        <p style={{ fontSize: '14px', color: '#9ca3af', margin: 0 }}>Last updated: June 26, 2026</p>
      </div>

      {/* ── BODY ── */}
      <div className="privacy-body-grid" style={{ maxWidth: '1100px', margin: '0 auto', padding: '56px 32px 96px', display: 'grid', gridTemplateColumns: '240px 1fr', gap: '64px' }}>

        {/* ── TOC (sticky sidebar) ── */}
        <nav className="privacy-toc" style={{ position: 'sticky', top: '84px', alignSelf: 'start' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '16px' }}>
            On this page
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', borderLeft: '2px solid #e5e7eb' }}>
            {SECTIONS.map(s => (
              <button
                key={s.id}
                onClick={() => { scrollTo(s.id); setActiveSection(s.id); }}
                style={{
                  textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer',
                  padding: '7px 0 7px 16px', fontSize: '13px', fontWeight: 500,
                  color: activeSection === s.id ? '#4f46e5' : '#6b7280',
                  borderLeft: activeSection === s.id ? '2px solid #4f46e5' : '2px solid transparent',
                  marginLeft: '-2px', transition: 'color 0.15s',
                }}
              >
                {s.title}
              </button>
            ))}
          </div>
        </nav>

        {/* ── CONTENT ── */}
        <div style={{ fontSize: '15px', lineHeight: 1.75, color: '#374151', maxWidth: '640px' }}>

          <p>
            By accessing or using CloudAIRambo ("the Service", "we", "our", or "us") at{' '}
            <a href="https://cloudairambo.com" style={{ color: '#4f46e5', fontWeight: 500 }}>cloudairambo.com</a>,
            you agree to be bound by these Terms of Service. If you don't agree, please don't use the Service.
          </p>

          <Section id="use" title="1. Use of Service">
            <p>
              CloudAIRambo provides invoicing, point-of-sale, inventory, and business management tools.
              You agree to use the Service only for lawful business purposes and in accordance with these terms.
            </p>
            <p>
              <strong>Eligibility.</strong> By using the Service, you confirm that you are at least 18 years of
              age (or the age of legal majority in your jurisdiction), and that you have the legal authority to
              agree to these Terms on behalf of yourself or the business or organization you represent. If you
              are creating an account or adding staff/employee accounts on behalf of a business, you confirm you
              are authorized to bind that business to these Terms.
            </p>
            <p>
              <strong>Supported Countries, Currencies & Phone Codes.</strong> The Service supports a defined
              list of countries, currencies, and phone country codes at any given time. If your country,
              currency, or phone code is not currently listed or available in the Service, please contact us at{' '}
              <a href="mailto:support@cloudairambo.com" style={{ color: '#4f46e5', fontWeight: 500 }}>
                support@cloudairambo.com
              </a>{' '}
              and we will do our best to add support for it. We do not guarantee a timeframe for adding new
              countries, currencies, or phone codes.
            </p>
          </Section>

          <Section id="account" title="2. Account Responsibility">
            <p>
              You are responsible for maintaining the confidentiality of your account credentials and for all
              activity that occurs under your account. Notify us immediately at{' '}
              <a href="mailto:support@cloudairambo.com" style={{ color: '#4f46e5', fontWeight: 500 }}>
                support@cloudairambo.com
              </a>{' '}
              if you suspect unauthorized access.
            </p>
            <p>
              <strong>Organizations, Outlets & Staff Accounts.</strong> The Service supports multi-outlet
              businesses, where an organization owner may create and manage multiple outlet locations and add
              staff, employee, manager, or cashier sub-accounts under their organization. The organization
              owner is solely responsible for: (i) determining who is granted access and at what permission
              level; (ii) all actions taken by any sub-account they create or authorize, including data
              entered, transactions processed, and messages sent; and (iii) promptly revoking access for staff
              who leave or should no longer have access. We are not responsible for disputes between an
              organization owner and their staff, or for actions taken by a sub-account that the owner failed
              to properly restrict or revoke.
            </p>
          </Section>

          <Section id="ip" title="2A. Intellectual Property">
            <p>
              The Service — including its software, source code, design, user interface, features, templates,
              logo, and the CloudAIRambo name and branding — is owned by us and is protected by applicable
              intellectual property laws. These Terms grant you a limited, non-exclusive, non-transferable
              license to access and use the Service for your business purposes. Nothing in these Terms transfers
              any ownership of the Service, its underlying technology, or our intellectual property to you. You
              may not copy, modify, reverse engineer, distribute, or create derivative works based on the
              Service, except to the extent expressly permitted by law.
            </p>
          </Section>

          <Section id="billing" title="3. Subscriptions & Billing">
            <p>
              Some features require a paid subscription. Subscription fees are billed in advance on a recurring
              basis (monthly or annually, as selected at checkout) and automatically renew unless cancelled
              before the next billing date.
            </p>
            <p>
              <strong>Payments &amp; International Gateway.</strong> Payments are processed through our
              third-party payment gateway partner(s), which may include support for international cards, UPI,
              net banking, and other regional payment methods. By subscribing, you authorize us and our payment
              processor to charge your selected payment method for all applicable fees. We do not store your
              full card or payment credentials — these are handled directly by our PCI-compliant payment
              gateway provider. Currency conversion, cross-border transaction fees, or charges levied by your
              bank or card issuer for international payments are your responsibility and are not controlled by
              CloudAIRambo.
            </p>
            <p>
              <strong>Non-Refundable Policy.</strong> All subscription fees, add-on module charges, and one-time
              charges are strictly non-refundable, in full or in part, once a billing cycle has started —
              including in cases of partial usage, unused features, downgrade mid-cycle, voluntary cancellation,
              or account suspension due to a violation of these Terms. No refunds or credits will be issued for
              partial subscription periods. This policy applies regardless of whether you actively used the
              Service during the billing period. Refunds will only be considered where required under applicable
              consumer protection law, or at our sole discretion in cases of a verified duplicate or erroneous
              charge caused by a technical error on our end, reported within 7 days of the charge.
            </p>
            <p>
              <strong>Failed Payments &amp; Chargebacks.</strong> If a payment fails or is declined, we may
              retry the charge, suspend access to paid features, or downgrade your account until payment is
              resolved. Initiating a chargeback or payment dispute without first contacting us at{' '}
              <a href="mailto:support@cloudairambo.com" style={{ color: '#4f46e5', fontWeight: 500 }}>
                support@cloudairambo.com
              </a>{' '}
              may result in immediate suspension of your account pending resolution.
            </p>
            <p>
              <strong>Taxes.</strong> All fees are exclusive of applicable taxes (GST, VAT, or other
              region-specific levies) unless stated otherwise, and such taxes will be added at checkout where
              legally required.
            </p>
            <p>
              We reserve the right to change subscription pricing at any time, with at least 30 days' notice
              provided via email or in-app notification before the change takes effect on your next billing
              cycle.
            </p>
          </Section>

          <Section id="data" title="4. Your Data">
            <p>
              You retain ownership of all data you enter into CloudAIRambo — invoices, client records, inventory,
              and everything else. You grant us a limited license to store and process that data solely to
              provide the Service to you.
            </p>
            <p>
              <strong>Data Retention After Cancellation.</strong> If you cancel your subscription or your
              account is terminated, your data is not automatically deleted. We retain it so you can reactivate
              your account at any time. Financial and transaction records (such as invoices and ledger entries)
              are retained for the period required under applicable Indian law (including the Companies Act and
              Income Tax Act), regardless of subscription status.
            </p>
            <p>
              <strong>Marketing & Promotional Use.</strong> We may use aggregated, anonymized, or non-personally
              identifiable usage statistics (such as the number of businesses using the Service, or general
              feature adoption trends) for marketing and promotional purposes. We may also request your explicit
              permission to feature your business name, logo, or a testimonial in our marketing materials — we
              will never do so without asking you first and receiving your consent. This does not apply to, and
              expressly excludes, biometric data, financial/transaction records, client data, or any other
              personal data entered into the Service by you or your customers, which are never used for
              marketing or promotional purposes under any circumstance.
            </p>
            <p>
              <strong>Deletion on Request.</strong> You may request deletion of your account and associated
              personal data at any time by contacting{' '}
              <a href="mailto:support@cloudairambo.com" style={{ color: '#4f46e5', fontWeight: 500 }}>
                support@cloudairambo.com
              </a>. We will notify you before proceeding and confirm the scope of what will be deleted. Once
              processed, data is removed from our active systems, though a compressed backup copy may be
              retained separately for a limited period, and financial records required to be retained by law
              will not be deleted even upon request. Once deleted from active systems, data cannot be recovered
              through normal means.
            </p>
            <p>
              <strong>Biometric Data.</strong> If you choose to enable optional attendance features such as
              face recognition or fingerprint scanning (used, for example, in gym or canteen member check-in),
              the Service will collect and store biometric identifiers (a facial descriptor and/or fingerprint
              credential) for the individuals enrolled. This data is collected only with the knowledge of the
              organization enabling the feature, and is used solely for the purpose of verifying attendance or
              identity within the Service. We do not sell, rent, share, or disclose biometric data to any
              third party, advertiser, or other organization for any purpose, including marketing, promotion,
              advertising, or product demonstrations. Biometric data is stored in our
              secured database with the same access controls and encryption-in-transit protections as the rest
              of your account data, and is never used for any purpose beyond the attendance/verification
              feature it was collected for. The organization enabling this feature is responsible for obtaining
              any consent required from the individual being enrolled (e.g. an employee or gym/canteen member)
              before doing so, in accordance with applicable law, including India's Digital Personal Data
              Protection Act. Biometric data can be deleted at any time by removing the associated member or
              employee profile, or by contacting{' '}
              <a href="mailto:support@cloudairambo.com" style={{ color: '#4f46e5', fontWeight: 500 }}>
                support@cloudairambo.com
              </a>.
            </p>
          </Section>

          <Section id="prohibited" title="5. Prohibited Conduct">
            <ul>
              <Li>Using the Service for any illegal or unauthorized purpose.</Li>
              <Li>Attempting to reverse engineer, decompile, or compromise the platform.</Li>
              <Li>Uploading malicious code or interfering with other users' access to the Service.</Li>
              <Li>Attempting to gain unauthorized access to accounts, systems, or data not your own.</Li>
              <Li>Using the Service to commit fraud, payment fraud, identity theft, or any financial crime.</Li>
              <Li>Circumventing, disabling, or manipulating billing, subscription, or licensing controls.</Li>
              <Li>Scraping, harvesting, or extracting data from the Service without authorization.</Li>
            </ul>
            <p style={{ marginTop: '14px' }}>
              <strong>Legal Action.</strong> We reserve the right to investigate any suspected violation of this
              section and to take appropriate action, including but not limited to immediate account
              suspension or termination without refund, reporting the conduct to law enforcement or regulatory
              authorities, and pursuing civil or criminal legal action to the fullest extent permitted by law.
              We will cooperate with law enforcement agencies investigating any illegal activity conducted
              through the Service, and may disclose relevant account information as required by a valid legal
              request.
            </p>
          </Section>

          <Section id="termination" title="6. Termination">
            <p>
              We reserve the right to suspend or terminate your account at our discretion if you violate these
              terms. You may cancel your account at any time from your account settings.
            </p>
          </Section>

          <Section id="force-majeure" title="6A. Force Majeure">
            <p>
              We will not be liable for any failure or delay in performing our obligations, or for any
              interruption, degradation, or unavailability of the Service, caused by events beyond our
              reasonable control. This includes, without limitation: natural disasters, fire, flood,
              earthquake, war, terrorism, civil unrest, government action or regulation, labor disputes,
              internet or telecommunications infrastructure failures, power outages, failures or outages of
              third-party cloud hosting, payment gateway, or messaging service providers we rely on to deliver
              the Service, and any other event of force majeure. We will make reasonable efforts to restore the
              Service as soon as practicable once such an event has passed.
            </p>
          </Section>

          <Section id="disclaimer" title="7. Disclaimer">
            <p>
              The Service is provided "as is" and "as available," without warranties of any kind, express or
              implied, including but not limited to warranties of merchantability, fitness for a particular
              purpose, or non-infringement.
            </p>
            <p>
              <strong>Third-Party Integrations.</strong> The Service relies on and integrates with third-party
              providers — including but not limited to WhatsApp messaging, SMS/OTP delivery, text-to-speech,
              barcode/QR scanning, payment gateways, and cloud hosting infrastructure — to deliver certain
              features. We do not control these third-party services and are not responsible for their
              availability, accuracy, security, or any changes they make to their APIs or terms. Any
              interruption, malfunction, or discontinuation of a third-party service that affects the Service
              is not attributable to us, and we do not guarantee uninterrupted access to any feature that
              depends on a third-party integration.
            </p>
            <p>
              <strong>WhatsApp Connectivity.</strong> To make WhatsApp-based notifications and messaging
              accessible to businesses who do not have their own official WhatsApp Business API access, the
              Service offers a convenience method of connecting your WhatsApp number by scanning a QR code,
              rather than requiring a formal WhatsApp Business API account. This method is not an official,
              sanctioned WhatsApp Business integration, and using it carries inherent risk, including but not
              limited to temporary or permanent restriction, suspension, or banning of the connected WhatsApp
              number by WhatsApp/Meta, at their sole discretion and outside our control. By choosing to connect
              your WhatsApp number through this method, you acknowledge and accept this risk entirely. We
              provide this option purely as a convenience for businesses that would otherwise have no
              affordable or accessible way to use WhatsApp messaging, and we are not liable for any loss,
              disruption, or damages arising from the restriction, suspension, banning, or disconnection of
              your WhatsApp number, whether used for personal or business purposes. We strongly recommend using
              a dedicated number for this purpose rather than your primary personal or business number.
            </p>
            <p>
              <strong>Auto-Translation & Text-to-Speech.</strong> Certain features of the Service, such as
              automatic translation of invoice or document content and text-to-speech audio generation, are
              powered by automated tools. These outputs are provided for convenience and may contain errors,
              inaccuracies, or mistranslations, particularly for specialized terminology, names, or regional
              language variations. You are responsible for reviewing any auto-translated or auto-generated
              content for accuracy before relying on it for official, legal, or financial purposes, and we are
              not liable for any loss or consequence arising from inaccuracies in this automatically generated
              content.
            </p>
            <p>
              <strong>Service Changes & No Uptime Guarantee.</strong> We may add, modify, suspend, or
              discontinue any feature, module, or part of the Service at any time, with or without notice,
              including as part of regular updates and improvements. We do not guarantee that the Service will
              be uninterrupted, error-free, or available 100% of the time, and we are not liable for any
              downtime, outage, or temporary unavailability of the Service, including any resulting loss of
              access to your data or business operations during that time.
            </p>
          </Section>

          <Section id="feedback" title="7. Feedback">
            <p>
              If you send us feedback, suggestions, feature requests, or bug reports, you grant us a
              perpetual, irrevocable, royalty-free license to use, implement, and incorporate that feedback
              into the Service in any way we see fit, without any obligation to compensate you or credit you
              for it.
            </p>
          </Section>

          <Section id="liability" title="8. Limitation of Liability">
            <p>
              To the fullest extent permitted by law, CloudAIRambo is not liable for any indirect, incidental,
              special, or consequential damages arising from your use of, or inability to use, the Service —
              including loss of profits, data, or business opportunities.
            </p>
          </Section>

          <Section id="indemnification" title="9. Indemnification">
            <p>
              You agree to indemnify, defend, and hold harmless CloudAIRambo, its owner(s), and affiliates from
              and against any claims, damages, losses, liabilities, costs, and expenses (including reasonable
              legal fees) arising out of or related to: your use or misuse of the Service; your violation of
              these Terms; your violation of any applicable law or third-party right; or any data, content, or
              information you upload, enter, or process through the Service — including data belonging to your
              customers, employees, or other third parties.
            </p>
            <p>
              This obligation applies regardless of whether the claim arises from your own actions or the
              actions of anyone using your account, including employees or staff members you have added to the
              Service.
            </p>
          </Section>

          <Section id="governing-law" title="10. Governing Law & Jurisdiction">
            <p>
              These Terms and any dispute arising out of or in connection with them, or your use of the
              Service, shall be governed by and construed in accordance with the laws of India, without regard
              to its conflict of law principles.
            </p>
            <p>
              You agree that any dispute, claim, or controversy arising out of or relating to these Terms or
              the Service shall be subject to the exclusive jurisdiction of the courts located in India, and
              you irrevocably submit to the jurisdiction of such courts.
            </p>
          </Section>

          <Section id="changes" title="11. Changes to These Terms">
            <p>
              We may update these Terms from time to time. We'll update the "Last updated" date above whenever we
              make changes. Continued use of CloudAIRambo after changes means you accept the updated Terms.
            </p>
          </Section>

          <Section id="general" title="12. General Provisions">
            <p>
              <strong>Severability.</strong> If any provision of these Terms is found by a court or authority
              of competent jurisdiction to be invalid, illegal, or unenforceable, that provision will be
              limited or eliminated to the minimum extent necessary, and the remaining provisions of these
              Terms will continue in full force and effect.
            </p>
            <p>
              <strong>Assignment.</strong> You may not assign or transfer these Terms, or any rights or
              obligations under them, without our prior written consent. We may assign or transfer these Terms,
              in whole or in part, without restriction — including in connection with a merger, acquisition,
              restructuring, or sale of assets.
            </p>
            <p>
              <strong>Entire Agreement.</strong> These Terms, together with our Privacy Policy and any other
              policies referenced herein, constitute the entire agreement between you and CloudAIRambo
              regarding the Service, and supersede any prior or contemporaneous agreements, communications, or
              understandings, whether written or oral.
            </p>
            <p>
              <strong>No Waiver.</strong> Our failure to enforce any right or provision of these Terms will not
              be considered a waiver of that right or provision.
            </p>
          </Section>

          <Section id="contact" title="13. Contact Us">
            <div style={{
              background: '#fafafa', border: '1px solid #e5e7eb', borderRadius: '14px',
              padding: '24px 28px', marginTop: '12px',
            }}>
              <p style={{ margin: 0 }}>
                Questions about these Terms? Reach us at{' '}
                <a href="mailto:support@cloudairambo.com" style={{ color: '#4f46e5', fontWeight: 600 }}>
                  support@cloudairambo.com
                </a>
              </p>
            </div>
          </Section>

        </div>
      </div>

      {/* ── FOOTER ── */}
      <footer style={{ background: '#f1f5f9', borderTop: '1px solid #e2e8f0' }}>
        <div style={{
          maxWidth: '1100px', margin: '0 auto', padding: '24px 20px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8,
        }}>
          <span style={{ fontSize: '13px', color: '#374151' }}>© 2026 CloudAIRambo. All rights reserved.</span>
          <a href="/privacy" style={{ fontSize: '13px', color: '#4b5563', textDecoration: 'none', fontWeight: 500 }}>
            Privacy Policy
          </a>
        </div>
      </footer>
    </div>
  );
}

// ─── Small helpers ──────────────────────────────────────────────────────────

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} style={{ marginTop: '48px', scrollMarginTop: '84px' }}>
      <h2 style={{ fontSize: '19px', fontWeight: 700, color: '#111827', marginBottom: '14px' }}>{title}</h2>
      {children}
    </section>
  );
}

function Li({ children }: { children: React.ReactNode }) {
  return (
    <li style={{ marginBottom: '8px', paddingLeft: '4px' }}>{children}</li>
  );
}
